import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../Store/Store';

type Tab = 'feedbacks' | 'access-logs' | 'geo' | 'products' | 'market-test';

interface EmployeeNavbarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  userPermissions: string[];
}

const TAB_DEFS: { id: Tab; label: string; icon: string }[] = [
  { id: 'feedbacks', label: 'Feedback', icon: '💬' },
  { id: 'products', label: 'Questions', icon: '📦' },
  { id: 'market-test', label: 'Market Test', icon: '🧪' },
  { id: 'access-logs', label: 'Access History', icon: '🗺️' },
  { id: 'geo', label: 'Registered Products', icon: '📍' },
];

const PERMISSION_MAP: Record<Tab, string> = {
  feedbacks: 'Feedbacks',
  products: 'Products',
  'market-test': 'MarketTest',
  'access-logs': 'Geo',
  geo: 'Geo',
};

const EmployeeNavbar: React.FC<EmployeeNavbarProps> = ({
  activeTab,
  onTabChange,
  userPermissions,
}) => {
  const { logout } = useAppContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const handleLogout = () => {
    logout();
    window.location.href = '/employee';
  };

  const visibleTabs = TAB_DEFS.filter(tab =>
    userPermissions.includes(PERMISSION_MAP[tab.id])
  );

  const handleTabChange = (tab: Tab) => {
    onTabChange(tab);
    setMenuOpen(false);
  };

  /* close drawer when clicking outside */
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  /* close drawer on resize to desktop */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMenuOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <nav className="mgmt-navbar" ref={navRef}>
      {/* Desktop tabs */}
      <ul className="mgmt-navbar__tabs">
        {visibleTabs.map(tab => (
          <li key={tab.id} className="mgmt-navbar__tab">
            <button
              className={`mgmt-navbar__tab-btn${activeTab === tab.id ? ' mgmt-navbar__tab-btn--active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="mgmt-navbar__tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Desktop logout */}
      <div className="mgmt-navbar__actions">
        <button
          onClick={handleLogout}
          className="mgmt-btn mgmt-btn--logout mgmt-btn--sm"
        >
          Logout
        </button>
      </div>

      {/* Mobile hamburger */}
      <button
        className={`mgmt-navbar__hamburger${menuOpen ? ' mgmt-navbar__hamburger--open' : ''}`}
        onClick={() => setMenuOpen(o => !o)}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
      >
        <span className="mgmt-navbar__hamburger-icon">
          <span />
          <span />
          <span />
        </span>
      </button>

      {/* Mobile drawer */}
      <div
        className={`mgmt-navbar__drawer${menuOpen ? ' mgmt-navbar__drawer--open' : ''}`}
        role="menu"
      >
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`mgmt-navbar__tab-btn${activeTab === tab.id ? ' mgmt-navbar__tab-btn--active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            role="menuitem"
          >
            <span className="mgmt-navbar__tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
        <div className="mgmt-navbar__drawer-logout">
          <button
            onClick={handleLogout}
            className="mgmt-btn mgmt-btn--logout"
            style={{ width: '100%', justifyContent: 'center' }}
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

export default EmployeeNavbar;
